import React, { useState } from 'react';
import { DetailsHeader, DetailsList, Selection } from 'office-ui-fabric-react/lib/DetailsList';
import { Field } from 'formik';
import PropTypes from 'prop-types';
import styles from './DynamicDetailsList.style';
import TextField from '../TextField';
import { pxToRem } from '../../utils';

const getName = (arrayName, rowIndex, column) => `${arrayName}.${rowIndex}.${column.key}`;
const nameHelper = arrayName => (index, column) => getName(arrayName, index, column);

const defaultRenderer = (arrayName, component, props) => (item, rowIndex, column) =>
  <Field name={getName(arrayName, rowIndex, column)} component={component} {...props} />;

const buildColumnsWithChangeHandlers = (columns, arrayName, arrayHelpers, values) => {
  // Order of renderer precedence:
  // 1. onRender
  // 2. getRenderer
  // 3. component
  // 4. TextField
  return columns.map(c => {
    let onRender;
    if (c.onRender) {
      onRender = c.onRender;
    } else {
      onRender = c.getRenderer
        ? c.getRenderer(arrayHelpers, { values, arrayName, nameHelper: nameHelper(arrayName) })
        : defaultRenderer(arrayName, c.component || TextField, { placeholder: 'Any', ...c.props });
    }
    return {
      ...c,
      onRender,
    };
  });
};

const DynamicDetailsList = ({
  columns,
  arrayHelpers,
  field: { name, value },
  form: { setFieldValue },
  enableRowReorder,
  ...otherProps
}) => {
  const [selection] = useState(new Selection());
  const dragDropEvents = {
    canDrop: () => enableRowReorder,
    canDrag: () => enableRowReorder,
    onDragEnter: () => styles.dragEnter,
    onDragLeave: () => {},
    onDrop: item => {
      const selectedItems = selection.getSelection();

      if (selectedItems && selectedItems.length > 0) {
        const insertIndex = value.indexOf(item);
        const unselectedItems = value.filter(itm => selectedItems.indexOf(itm) === -1);

        unselectedItems.splice(insertIndex, 0, ...selectedItems);
        setFieldValue(name, unselectedItems);
      }
    },
  };

  const reorderProps = enableRowReorder
    ? { selection, dragDropEvents, selectionPreservedOnEmptyClick: true }
    : {};

  return (
    <DetailsList
      styles={{
        root: {
          paddingTop: pxToRem(12),
        },
      }}
      onRenderDetailsHeader={props => <DetailsHeader {...props} className={styles.tableColumn} />}
      columns={buildColumnsWithChangeHandlers(columns, name, arrayHelpers, value)}
      items={value}
      {...reorderProps}
      {...otherProps}
    />
  );
};

DynamicDetailsList.propTypes = {
  enableRowReorder: PropTypes.bool,
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  arrayHelpers: PropTypes.object.isRequired,
  form: PropTypes.shape({
    setFieldValue: PropTypes.func.isRequired,
  }).isRequired,
  field: PropTypes.shape({
    name: PropTypes.string.isRequired,
    value: PropTypes.arrayOf(PropTypes.object).isRequired,
  }).isRequired,
};

DynamicDetailsList.defaultProps = {
  enableRowReorder: false,
};

export default DynamicDetailsList;
